import { useEffect, useState } from 'react';
import { awaitConsent, onConsentDone, openConsentWindow, returnPath } from './storage-connect';
import type { StorageControls } from './StorageFields';
import type { PaigeDestination, PaigeDestinationsBridge } from './paige.global';

/**
 * A storage destination's connection as the sheet drives it: the destination as last read from
 * Paige, read again whenever the consent window reports or this window gets the focus back (the
 * consent window closing gives it back), and Connect / Disconnect. Errors are Paige's reasons.
 */
export function useStorageControls(
  bridge: PaigeDestinationsBridge,
  existing: PaigeDestination | undefined,
  onError: (reason: string) => void,
): StorageControls {
  const [current, setCurrent] = useState(existing);
  const [busy, setBusy] = useState(false);
  const id = existing?.id;
  useRefreshOnConsent(bridge, id, setCurrent);
  const attempt = (action: () => Promise<void>) => {
    setBusy(true);
    action()
      .catch((failure: unknown) =>
        onError(failure instanceof Error ? failure.message : 'Paige could not reach the account'),
      )
      .finally(() => setBusy(false));
  };
  return {
    current,
    busy,
    connect: () => {
      if (!id) return;
      const consent = openConsentWindow();
      attempt(() => connectIn(bridge, id, consent));
    },
    disconnect: () => id && attempt(async () => setCurrent(await bridge.disconnect(id))),
  };
}

/** Reads the destination again when the consent page reports (message or broadcast), or when this window gets the focus back. */
function useRefreshOnConsent(
  bridge: PaigeDestinationsBridge,
  id: string | undefined,
  setCurrent: (destination: PaigeDestination | undefined) => void,
) {
  useEffect(() => {
    if (!id) return;
    const refresh = () =>
      void bridge
        .list()
        .then((rows) => setCurrent(rows.find((row) => row.id === id)))
        .catch(() => undefined);
    const stop = onConsentDone(refresh);
    window.addEventListener('focus', refresh);
    return () => {
      stop();
      window.removeEventListener('focus', refresh);
    };
  }, [bridge, id, setCurrent]);
}

/** Asks Paige for the consent URL and sends the window opened in the click there; closes it if Paige refuses. */
export async function connectIn(
  bridge: PaigeDestinationsBridge,
  id: string,
  consent: Window | null,
): Promise<void> {
  try {
    const url = await bridge.connect(id, returnPath(id));
    void awaitConsent(consent, url);
  } catch (failure) {
    consent?.close();
    throw failure;
  }
}
